import { Attributes, SpanKind, SpanStatusCode, trace } from "@opentelemetry/api";

import { ROOT_SPAN_NAME, WEB_TRACER_VERSION } from "../constants";
import CONV from "../semantic-conventions";
import {
  ErrorInstrumentationOptions,
  InteractionConfig,
  NetworkInstrumentationOptions,
  ProviderType,
  RootSpan,
} from "../types";
import { getFoldedPath, onNavigation } from "../utils";
import { sanitizeAttributes } from "../utils/attributes";
import { error, forceLog, log, updateLoggerViewId, warn } from "../utils/logger";
import { ErrorCollector } from "./error";
import { UserInteractionInstrumentation } from "./interactions";
import { NetworkInstrumentation } from "./network";
import { WebVitals } from "./web-vitals";

type ViewLogLevel = "info" | "warn" | "error";

type ViewSnapshot = {
  viewId?: string;
  attributes: Record<string, unknown>;
};

const emitViewLog = (
  level: ViewLogLevel,
  message: string,
  attributes: Record<string, unknown> = {},
  err?: unknown,
) => {
  const payload = {
    message,
    attributes: {
      "l9rum.view.component": "ViewRootSpanManager",
      ...attributes,
    },
    ...(err ? { error: err } : {}),
  };

  if (level === "warn") {
    warn(payload);
    return;
  }
  if (level === "error") {
    error(payload);
    return;
  }
  log(payload);
};

type ViewManagerConfig = {
  errors?: ErrorInstrumentationOptions;
  network?: NetworkInstrumentationOptions;
  interactions?: InteractionConfig;
};

export class ViewRootSpanManager {
  private provider: ProviderType;
  private tracer: any;
  private currentRootSpan?: RootSpan;
  private webVitals: WebVitals;
  private errorCollector: ErrorCollector;
  private interactions?: UserInteractionInstrumentation;
  private network?: NetworkInstrumentation;
  private networkConfig?: NetworkInstrumentationOptions;
  private _pageHideListener?: () => void;
  private config: ViewManagerConfig;

  private handleNetworkError = (error: unknown, context: Record<string, any> = {}) => {
    this.errorCollector.captureNetworkError(error, context);
  };

  constructor(provider: ProviderType, config: ViewManagerConfig = {}) {
    this.provider = provider;
    this.config = config;
    this.tracer = provider.getTracer("web-tracer", WEB_TRACER_VERSION);
    this.webVitals = new WebVitals(this.tracer);
    this.errorCollector = new ErrorCollector(this.tracer, config.errors);
    this.networkConfig = config.network;
    this.initializeInstruments();
  }

  public init() {
    emitViewLog("info", "[L9RUM View] Initializing root span management", {
      "l9rum.view.phase": "init",
    });
    try {
      this._startRootSpan();
    } catch (e) {
      emitViewLog(
        "error",
        "[L9RUM View] Failed to start root span during init",
        {
          "l9rum.view.phase": "init",
          "l9rum.view.guard": "start_root_span",
        },
        e,
      );
    }
    this._initEvents();
  }

  private initializeInstruments() {
    // User interactions (clicks, scrolls, keyboard, etc.)
    this.interactions = new UserInteractionInstrumentation(this.tracer, this.config.interactions);
    // Network (fetch/xhr + resources)
    this.network = new NetworkInstrumentation(this.tracer, this.networkConfig);
  }

  private _initEvents = () => {
    this._initNavigationEvents();
    this._initSessionEvents();
    this._initPageResumeEvents();
  };

  private _initNavigationEvents = () => {
    // On navigation, end the current root span and start a new one
    try {
      onNavigation(async () => {
        emitViewLog(
          "info",
          "[L9RUM View] Navigation detected, ending previous root span and starting new root span",
          {
            "l9rum.view.phase": "navigation",
          },
        );
        this._endRootSpan();
        try {
          this._startRootSpan();
        } catch (e) {
          emitViewLog(
            "error",
            "[L9RUM View] Failed to start root span after navigation",
            {
              "l9rum.view.phase": "navigation",
              "l9rum.view.guard": "start_root_span",
            },
            e,
          );
        }
      });
    } catch (e) {
      emitViewLog(
        "warn",
        "[L9RUM View] Failed to attach navigation listeners; view may not rotate on SPA nav",
        {
          "l9rum.view.phase": "init",
          "l9rum.view.guard": "navigation_listener",
        },
        e,
      );
    }
  };

  private _initPageResumeEvents = () => {
    try {
      // BFCache restore and general resume
      window.addEventListener("pageshow", () => {
        emitViewLog("info", "[L9RUM View] pageshow detected", {
          "l9rum.view.phase": "resume",
          "l9rum.view.guard": "pageshow",
        });
        this.ensureRootSpan("pageshow detected");
      });
    } catch (e) {
      emitViewLog(
        "warn",
        "[L9RUM View] Failed to attach pageshow listener",
        {
          "l9rum.view.phase": "resume",
          "l9rum.view.guard": "pageshow_listener",
        },
        e,
      );
    }
    try {
      window.addEventListener("focus", () => {
        emitViewLog("info", "[L9RUM View] focus detected", {
          "l9rum.view.phase": "resume",
          "l9rum.view.guard": "focus",
        });
        this.ensureRootSpan("window focus detected");
      });
    } catch (e) {
      emitViewLog(
        "warn",
        "[L9RUM View] Failed to attach focus listener",
        {
          "l9rum.view.phase": "resume",
          "l9rum.view.guard": "focus_listener",
        },
        e,
      );
    }
  };

  private _initSessionEvents = () => {
    try {
      if (typeof window !== "undefined") {
        window.addEventListener("l9rum:session_rollover", () => {
          emitViewLog(
            "info",
            "[L9RUM View] Session rollover detected, ending previous root span and starting new root span",
            {
              "l9rum.view.phase": "session_rollover",
            },
          );
          this._endRootSpan();
          this._startRootSpan();
        });
        emitViewLog("info", "[L9RUM View] session_rollover listener attached", {
          "l9rum.view.phase": "init",
          "l9rum.view.guard": "session_rollover_listener",
        });
      }
    } catch (e) {
      emitViewLog(
        "warn",
        "[L9RUM View] Failed to attach session_rollover listener",
        {
          "l9rum.view.phase": "init",
          "l9rum.view.guard": "session_rollover_listener",
        },
        e,
      );
    }
  };

  private _startRootSpan() {
    // Unsubscribe from web vitals and error collector
    this.webVitals.unsubscribe();
    this.errorCollector.unsubscribe();
    this.interactions?.unsubscribe();

    const rootSpanAttributes = this._getRootSpanAttributes();
    this.currentRootSpan = this.tracer.startSpan(ROOT_SPAN_NAME, {
      kind: SpanKind.INTERNAL,
      attributes: rootSpanAttributes,
    });
    emitViewLog("info", "[L9RUM View] Started root span", {
      "l9rum.view.phase": "root_span",
      "l9rum.view.attributes": JSON.stringify(rootSpanAttributes),
    });
    try {
      const vid = this.currentRootSpan?.spanContext().traceId;
      if (vid) {
        try {
          this.currentRootSpan?.setAttribute(CONV.VIEW_ID, vid);
        } catch (attrErr) {
          emitViewLog(
            "warn",
            "[L9RUM View] Failed to set view.id attribute",
            {
              "l9rum.view.phase": "root_span",
              "l9rum.view.guard": "view_id_attribute",
            },
            attrErr,
          );
        }
        updateLoggerViewId(vid);
      }
    } catch (e) {
      emitViewLog(
        "warn",
        "[L9RUM View] Failed to update logger view id",
        {
          "l9rum.view.phase": "root_span",
          "l9rum.view.guard": "logger_view_id",
        },
        e,
      );
    }

    // Start adding web vitals and error events to the root span
    if (this.currentRootSpan) {
      // Default to OK status
      if (typeof this.currentRootSpan.setStatus === "function") {
        this.currentRootSpan.setStatus({ code: SpanStatusCode.OK });
      }

      try {
        this.webVitals.subscribe(this.currentRootSpan);
      } catch (e) {
        emitViewLog(
          "warn",
          "[L9RUM View] WebVitals subscribe failed; continuing without web vitals",
          {
            "l9rum.view.phase": "root_span",
            "l9rum.view.guard": "web_vitals_subscribe",
          },
          e,
        );
      }

      try {
        this.errorCollector.subscribe(this.currentRootSpan);
      } catch (e) {
        emitViewLog(
          "warn",
          "[L9RUM View] Error collector subscribe failed; continuing",
          {
            "l9rum.view.phase": "root_span",
            "l9rum.view.guard": "error_collector_subscribe",
          },
          e,
        );
      }

      try {
        this.interactions?.subscribe(this.currentRootSpan);
      } catch (e) {
        emitViewLog(
          "warn",
          "[L9RUM View] User interactions subscribe failed; continuing",
          {
            "l9rum.view.phase": "root_span",
            "l9rum.view.guard": "interactions_subscribe",
          },
          e,
        );
      }

      try {
        this.network?.subscribe(this.currentRootSpan, this.handleNetworkError);
      } catch (e) {
        emitViewLog(
          "warn",
          "[L9RUM View] Network subscribe failed; continuing",
          {
            "l9rum.view.phase": "root_span",
            "l9rum.view.guard": "network_subscribe",
          },
          e,
        );
      }
    }
  }

  private _endRootSpan() {
    if (this.currentRootSpan) {
      try {
        this.errorCollector.flush("view_end");
      } catch (e) {
        emitViewLog(
          "warn",
          "[L9RUM View] Failed to flush error collector before ending root span",
          {
            "l9rum.view.phase": "root_span_end",
            "l9rum.view.guard": "error_flush",
          },
          e,
        );
      }
      emitViewLog("info", "[L9RUM View] Ending root span", {
        "l9rum.view.phase": "root_span_end",
      });
      this.currentRootSpan.end();
      try {
        updateLoggerViewId(undefined);
      } catch (e) {
        emitViewLog(
          "warn",
          "[L9RUM View] Failed to clear logger view id",
          {
            "l9rum.view.phase": "root_span_end",
            "l9rum.view.guard": "logger_view_id_clear",
          },
          e,
        );
      }
      this.webVitals.unsubscribe();
      this.errorCollector.unsubscribe();
      this.interactions?.unsubscribe();
      this.network?.unsubscribe();
      this.currentRootSpan = undefined;
    }
  }

  private _getRootSpanAttributes() {
    try {
      const location = window.location;
      return {
        origin: location.origin,
        path: getFoldedPath(location.pathname),
        [CONV.PAGE_URL]: location.href,
        [CONV.PAGE_HASH]: location.hash,
        [CONV.PAGE_ROUTE]: location.pathname,
        [CONV.PAGE_HOSTNAME]: location.hostname,
        [CONV.PAGE_SEARCH]: location.search,
        [CONV.URL_PATH]: location.pathname,
      };
    } catch {
      return {} as any;
    }
  }

  protected onShutdown(): void {
    if (typeof document !== "undefined") {
      if (this._pageHideListener) {
        document.removeEventListener("pagehide", this._pageHideListener);
      }
    }
  }

  public endForExit() {
    // On hard exits, timers may not run. Do not wait; end immediately.
    // Any CLS already observed has been attached via WebVitals.
    // Fire-and-forget; no awaits to avoid being cancelled by page teardown.
    try {
      this._endRootSpan();
    } catch (e) {
      emitViewLog(
        "warn",
        "[L9RUM View] Failed to end root span during exit",
        {
          "l9rum.view.phase": "exit",
          "l9rum.view.guard": "end_for_exit",
        },
        e,
      );
    }
  }

  public ensureRootSpan(reason?: string) {
    if (this.currentRootSpan) {
      return;
    }

    if (reason) {
      emitViewLog("info", `[L9RUM View] ${reason}; starting new root span`, {
        "l9rum.view.phase": "ensure_root_span",
        "l9rum.view.guard": "reasoned_start",
      });
    } else {
      emitViewLog("info", "[L9RUM View] Starting new root span", {
        "l9rum.view.phase": "ensure_root_span",
      });
    }

    this._startRootSpan();
  }

  public captureError(error: unknown, context: Record<string, any> = {}) {
    if (!this.currentRootSpan) {
      this.ensureRootSpan("Manual error capture requested");
    }
    this.errorCollector.captureManualError(error, context);
  }

  public recordCustomEvent(eventName: string, attributes: Attributes = {}, timestamp?: number) {
    if (!this.currentRootSpan) {
      this.ensureRootSpan("Custom event recorded");
    }

    if (!this.currentRootSpan) {
      emitViewLog("warn", "[L9RUM View] Unable to record custom event; no active view span", {
        "l9rum.view.phase": "custom_event",
        "l9rum.view.guard": "missing_root_span",
        "l9rum.view.event_name": eventName,
      });
      return;
    }

    let eventAttributes: Attributes = attributes || {};

    const viewSnapshot = this.getCurrentViewSnapshot();
    if (viewSnapshot) {
      try {
        const sanitizedViewAttributes = sanitizeAttributes(viewSnapshot.attributes || {});
        if (viewSnapshot.viewId) {
          sanitizedViewAttributes[CONV.VIEW_ID] = viewSnapshot.viewId;
        }
        eventAttributes = {
          ...sanitizedViewAttributes,
          ...eventAttributes,
        };
        if (viewSnapshot.viewId) {
          eventAttributes[CONV.VIEW_ID] = viewSnapshot.viewId;
        }
      } catch (err) {
        emitViewLog(
          "warn",
          "[L9RUM View] Failed to merge snapshot attributes for custom event",
          {
            "l9rum.view.phase": "custom_event",
            "l9rum.view.guard": "snapshot_merge",
            "l9rum.view.event_name": eventName,
          },
          err,
        );
      }
    }

    try {
      if (typeof this.currentRootSpan.addEvent === "function") {
        if (typeof timestamp === "number") {
          this.currentRootSpan.addEvent(eventName, eventAttributes, timestamp);
        } else {
          this.currentRootSpan.addEvent(eventName, eventAttributes);
        }
      }
    } catch (e) {
      emitViewLog(
        "warn",
        "[L9RUM View] Failed to record custom event on view span",
        {
          "l9rum.view.phase": "custom_event",
          "l9rum.view.guard": "add_event_failed",
          "l9rum.view.event_name": eventName,
        },
        e,
      );
    }

    // Send custom event as a log in addition to trace
    try {
      const activeSpan = trace.getActiveSpan();
      const ctxAttrs: Record<string, string> = {};
      if (activeSpan && typeof activeSpan.spanContext === "function") {
        const sc = activeSpan.spanContext();
        if (sc?.traceId) ctxAttrs["trace.id"] = sc.traceId;
        if (sc?.spanId) ctxAttrs["span.id"] = sc.spanId;
      }

      forceLog({
        message: `Custom event: ${eventName}`,
        attributes: {
          ...eventAttributes,
          ...ctxAttrs,
          "event.type": "custom",
          "event.name": eventName,
        },
      });
    } catch (err) {
      emitViewLog(
        "warn",
        "[L9RUM View] Failed to emit custom event log",
        {
          "l9rum.view.phase": "custom_event",
          "l9rum.view.guard": "log_emission_failed",
          "l9rum.view.event_name": eventName,
        },
        err,
      );
    }
  }

  public getCurrentViewSnapshot(): ViewSnapshot | undefined {
    if (!this.currentRootSpan) {
      return undefined;
    }

    let attributes: Record<string, unknown> = {};
    try {
      const rawAttributes = (this.currentRootSpan as any)?.attributes;
      if (rawAttributes && typeof rawAttributes === "object") {
        attributes = { ...rawAttributes };
      }
    } catch (e) {
      emitViewLog(
        "warn",
        "[L9RUM View] Failed to snapshot view attributes; continuing without them",
        {
          "l9rum.view.phase": "snapshot",
          "l9rum.view.guard": "attributes_read",
        },
        e,
      );
    }

    const viewId = (() => {
      try {
        return this.currentRootSpan?.spanContext().traceId;
      } catch {
        return undefined;
      }
    })();

    return {
      viewId,
      attributes,
    };
  }
}
